import ms from "ms";
import { inject, injectable } from "inversify";
import type { QueryResponseType, SuccessfullQueryResponse } from "@whatslly/types/QueryResponse";
import type { SelectedContactType } from "@whatslly/types/SelectedContactType";
import type { PersonType } from "@whatslly/types/Person";
import type { UserType } from "@whatslly/types/User";
import type { ThreadMessageTypeType } from "@whatslly/types/ThreadMessageQueueItem";
import type { CreateLeadResponseType } from "@whatslly/types/CreateLeadResponse";
import type { CreateContactResponseType } from "@whatslly/types/CreateContactResponse";
import { WidgetCommands } from "@whatslly/commons/widgetCommands";
import { backgroundClient } from "@whatslly/commons/background/client";
import type { IMSelectorsUnion } from "@whatslly/types/integrations/im/IMSelectorsTypes";
import { FrontLogger } from "@whatslly/commons/frontLogger/frontLogger";
import { makeAutoObservable, makeObservable, observable, runInAction } from "mobx";
import { BgCommands } from "@whatslly/commons/bgCommands";
import { ChunkedQueue } from "@whatslly/commons/chunkedQueue";
import { WebScriptCommands } from "@whatslly/commons/webScriptCommands";
import type { SyncEligibleQuery } from "@whatslly/instant-messaging/messageSyncUtils";
import { getCompactQuery, isSyncEligibleQuery, scrapedMessageToAnalyticsMessage, shouldSyncQuery, toMessageQueueItem } from "@whatslly/instant-messaging/messageSyncUtils";
import { webScriptBackend } from "./webScriptBackend";
import { DEFAULT_SCRAPER_SETTINGS } from "./whatsapp/messageScraper";
import type { WhatsllyGroupType } from "@whatslly/types/WhatsllyGroup";
import type { Translations } from "./i18nService";
import { widgetClient } from "@whatslly/commons/widgetClient";
import type { EmittedMessage, IMessageEmitter, IMessageEmitterInstance, SerializedMessage, MessageQueueItem } from "@whatslly/types/instant-messaging/messagesEmitter";
import { AllChats, InjectMessageEmitter } from "@whatslly/types/instant-messaging/messagesEmitter";
import type { IDOMInjectorService, SelectiveSyncStore, SelectiveEntireDaySyncStore } from "@whatslly/instant-messaging/commons/IDOMInjectorService";
import { IDOMInjectorServiceSymbol } from "@whatslly/instant-messaging/commons/IDOMInjectorService";
import type { IImTemplatesStore } from "@whatslly/instant-messaging/commons/IImTemplatesStore";
import { ImTemplatesStoreSymbol } from "@whatslly/instant-messaging/commons/IImTemplatesStore";
type SinglePersonQuery = Exclude<SyncEligibleQuery, { person: PersonType[] }>;

export type AutoSyncStatus = "auto" | "selective" | "off";

type ServiceState = {
  query: QueryResponseType | CreateContactResponseType | CreateLeadResponseType;
  selectedContact: SelectedContactType;
  syncMode: AutoSyncStatus;
};

@injectable()
export class MessageSyncService {
  private scraper?: IMessageEmitterInstance;
  private abortController?: AbortController;
  private pendingDOMInstrumentation: {
    synced: string[];
    invalid: SerializedMessage[];
    selective: SelectiveSyncStore[];
    entireDay: SelectiveEntireDaySyncStore[];
  } = { synced: [], invalid: [], selective: [], entireDay: [] };
  private selectDayCheckboxes = new Map<number, SelectiveEntireDaySyncStore>();
  private selectedMessages = new Set<SelectiveSyncStore>();
  private trackedMessageIds = new Map<string, Set<string>>();
  private logger: FrontLogger;
  private selectors: IMSelectorsUnion | undefined;
  private i18n?: Translations;

  public sendQueue = new ChunkedQueue<MessageQueueItem>(
    async (threads) => {
      const userPhone = this.MessageEmitter.getMe()?.phone;

      if (!userPhone) {
        return;
      }

      const res = await backgroundClient.send(BgCommands.SAVE_THREADS, {
        userPhone,
        threads,
      });

      widgetClient.send(WidgetCommands.MESSAGES_SYNCED, { success: Boolean(res?.success) });
    },
    { chunkSize: 20, interval: ms("1m") }
  );

  constructor(
    @inject(FrontLogger) private frontLogger: FrontLogger,
    @inject(ImTemplatesStoreSymbol) private readonly templatesStore: IImTemplatesStore,
    @inject(InjectMessageEmitter) private readonly MessageEmitter: IMessageEmitter<any, any>,
    @inject(IDOMInjectorServiceSymbol) private readonly domInjectorService: IDOMInjectorService
  ) {
    this.logger = this.frontLogger.getLogger("message-sync-service");

    makeObservable(this, {
      selectedMessages: observable.shallow,
    } as any);
  }

  private instrumentDOM() {
    // these are messages that need DOM instrumentation.
    // when we were notified about the message, they were not in the DOM yet.
    const { synced, invalid, selective, entireDay } = this.pendingDOMInstrumentation;
    this.pendingDOMInstrumentation = { synced: [], invalid: [], selective: [], entireDay: [] };
    // For now, we disabled this feature. it's commented on purpose. DO-NOT remove it.
    // synced.forEach((messageId) => this.markAsSynced(messageId));
    invalid.forEach((message) => this.markAsInvalidMessage(message));
    selective.forEach((store) => this.addSelectMessageChcekbox(store));
    entireDay.forEach((store) => this.mountSelectDayCheckbox(store));
  }

  private markAsSynced(messageId: string) {
    const resMessageId = this.domInjectorService.markAsSynced(messageId, this.selectors);
    if (resMessageId) {
      this.pendingDOMInstrumentation.synced.push(resMessageId);
    }
  }

  private markAsInvalidMessage(message: SerializedMessage) {
    const resMessage = this.domInjectorService.markAsInvalidMessage(
      message,
      this.#previousState?.query.user?.uploadFile?.maxFileSizeBytes || 0,
      this.i18n,
      this.selectors
    );
    if (resMessage) {
      this.pendingDOMInstrumentation.invalid.push(resMessage);
    }
  }

  private cleanupMessagesDOMInstrumentations(cleanup = { selected: true, synced: true }) {
    this.domInjectorService.cleanupMessagesDOMInstrumentations(cleanup, this.selectors);
    if (cleanup.synced) {
      this.pendingDOMInstrumentation.synced = [];
      this.pendingDOMInstrumentation.invalid = [];
    }
    if (cleanup.selected) {
      this.pendingDOMInstrumentation.selective = [];
      this.pendingDOMInstrumentation.entireDay = [];
      this.selectDayCheckboxes.clear();
    }
  }

  private static getAllowedMediaMessageTypes(user?: UserType) {
    // eslint-disable-next-line no-useless-assignment
    let mediaMessageTypes = undefined;
    if (Array.isArray(user?.uploadFile?.allowMessageTypes)) {
      mediaMessageTypes = new Set(user?.uploadFile?.allowMessageTypes);
    } else if (user?.allowToSyncFiles) {
      mediaMessageTypes = DEFAULT_SCRAPER_SETTINGS.mediaMessageTypes;
    } else {
      mediaMessageTypes = new Set<ThreadMessageTypeType>();
    }
    return mediaMessageTypes;
  }
  private static getAllowedVcardMessageTypes(user?: UserType) {
    return user?.allowToSyncVcard ? DEFAULT_SCRAPER_SETTINGS.vcardMessageTypes : new Set<ThreadMessageTypeType>();
  }

  public static getListenerSettings(user: UserType) {
    const mediaMessageMimeTypes =
      user?.uploadFile?.autoSyncMediaMessageMimeTypes && Array.isArray(user.uploadFile.autoSyncMediaMessageMimeTypes)
        ? new Set(user.uploadFile.autoSyncMediaMessageMimeTypes)
        : undefined;
    const mediaMessageTypes = MessageSyncService.getAllowedMediaMessageTypes(user);
    const vcardMessageTypes = MessageSyncService.getAllowedVcardMessageTypes(user);
    const eventMessageTypes = DEFAULT_SCRAPER_SETTINGS.eventMessageTypes;
    const textMessageTypes = DEFAULT_SCRAPER_SETTINGS.textMessageTypes;

    let maxMessageAge = Number.isInteger(user.syncronizeLastDays) ? ms(`${user.syncronizeLastDays} days`) : DEFAULT_SCRAPER_SETTINGS.maxMessageAge;
    if (user.disableRetroactiveSync && user.createdAt) {
      maxMessageAge = Math.min(maxMessageAge, Date.now() - user.createdAt);
    }

    const maxFileSizeBytes = typeof user?.uploadFile?.maxFileSizeBytes === "number" ? user?.uploadFile?.maxFileSizeBytes : undefined;
    const emitAckChanges = user?.emitAckChanges;
    const scrollToEarliestMessage = user?.scrollToEarliestMessage;
    const syncNewsLetterMessages = user?.allow_whatsapp_channels;
    const syncStatusMessages = user?.collect_whatsapp_status_messages;
    return {
      blockedPhones: user.phones_blocked_from_sync,
      collectGroupParticipants: user.collect_messages_group_participants,
      scrollToEarliestMessage,
      mediaMessageTypes,
      vcardMessageTypes,
      eventMessageTypes,
      textMessageTypes,
      mediaMessageMimeTypes,
      maxFileSizeBytes,
      maxMessageAge,
      emitAckChanges,
      syncNewsLetterMessages,
      syncStatusMessages,
    };
  }

  public async commitSelectiveSyncMessages() {
    const msgs = Array.from(this.selectedMessages);
    widgetClient.send(WidgetCommands.MESSAGES_SELECTED, { messages: [] });
    this.selectedMessages.clear();
    await Promise.all(msgs.map((store) => store.commit()));
    await this.sendQueue.flush();
  }

  private toggleMessageSelection(store: SelectiveSyncStore, value: boolean) {
    if (value) {
      this.selectedMessages?.add(store);
    } else {
      this.selectedMessages?.delete(store);
    }

    this.domInjectorService.toggleMessageSelection(store, value, this.selectedMessages, this.selectors);
  }

  private addSelectDayCheckbox(messageStore: SelectiveSyncStore) {
    const { message, disabled } = messageStore;
    let store = this.selectDayCheckboxes.get(message.startOfDay);
    if (store) {
      runInAction(() => !disabled && store!.messages.push(messageStore));
    } else {
      const title = this.MessageEmitter.relativeDateStr(message.startOfDay)?.toString().toUpperCase() ?? "";
      store = makeAutoObservable(
        {
          mounted: false,
          title,
          messages: disabled ? [] : [messageStore],
          get value() {
            return Boolean(this.messages.length) && this.messages.every((m: SelectiveSyncStore) => m.value);
          },
          get disabled() {
            return !this.messages.length;
          },
          update(value: boolean) {
            this.messages.forEach((message) => message.update(value));
          },
        },
        {
          messages: observable.shallow,
        }
      );
      this.selectDayCheckboxes.set(message.startOfDay, store);
    }

    this.mountSelectDayCheckbox(store);
  }

  private mountSelectDayCheckbox(store: SelectiveEntireDaySyncStore) {
    const resStore = this.domInjectorService.mountSelectDayCheckbox(store, this.selectors);
    if (resStore) {
      this.pendingDOMInstrumentation.entireDay.push(resStore);
    }
  }

  private getSelectiveSyncStore({
    message,
    allowedMessageTypes = new Set(DEFAULT_SCRAPER_SETTINGS.textMessageTypes),
    ageLimit = DEFAULT_SCRAPER_SETTINGS.maxMessageAge,
    query,
    selectedContact,
    syncedMessages = new Map(),
  }: {
    message: EmittedMessage;
    allowedMessageTypes?: Set<ThreadMessageTypeType>;
    ageLimit?: number;
    syncedMessages?: Map<string, number | null>;
    query?: SyncEligibleQuery;
    selectedContact?: SelectedContactType;
  }): SelectiveSyncStore {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const service = this;
    const isWithinAgeLimit = message.timestamp > Date.now() - ageLimit;
    if (!allowedMessageTypes.has(message.type as ThreadMessageTypeType) && message.text) {
      message.type = "chat" as ThreadMessageTypeType;
    }
    const allowedType = allowedMessageTypes.has(message.type as ThreadMessageTypeType);
    return makeAutoObservable(
      {
        message,
        testId: `checkbox-${message.serializedMessageId}`,
        get value() {
          return syncedMessages.has(message.shortId) || syncedMessages.has(message.serializedMessageId) || service.selectedMessages.has(this);
        },
        get disabled() {
          return syncedMessages.has(message.shortId) || syncedMessages.has(message.serializedMessageId) || !isWithinAgeLimit || !allowedType;
        },
        get showDisableTooltip() {
          return !isWithinAgeLimit && !syncedMessages.has(message.shortId) && !syncedMessages.has(message.serializedMessageId);
        },
        update(value: boolean) {
          service.toggleMessageSelection(this, value);
        },
        async commit() {
          if (!query || !selectedContact) {
            return;
          }
          await service.emitMessage(message, query, selectedContact, syncedMessages);
          service.trackMessage(message, true, query);
        },
      },
      {
        message: false,
      }
    );
  }

  private addSelectMessageChcekbox(store: SelectiveSyncStore) {
    const res = this.domInjectorService.addSelectMessageChcekbox(store, this.selectors);
    if (!res) {
      return;
    }
    if (res.pending) {
      this.pendingDOMInstrumentation.selective.push(res.store);
      return;
    }
    this.addSelectDayCheckbox(res.store);
  }

  private async selectAllMessagesWithText() {
    this.abortController?.abort();
    this.abortController = undefined;
    this.scraper = undefined;
    this.selectedMessages.clear();
    this.cleanupMessagesDOMInstrumentations({ selected: true, synced: false });
    if (!this.selectors) {
      return;
    }
    this.MessageEmitter.inject(this.selectors);
    this.abortController = new AbortController();
    const chatModel = this.MessageEmitter.getActiveChat();
    if (!chatModel) {
      return;
    }
    const textMessageTypes = DEFAULT_SCRAPER_SETTINGS.textMessageTypes;
    this.scraper = new this.MessageEmitter(
      chatModel,
      {
        listenToNewMessages: true,
        maxMessageAge: Infinity,
        scrollToEarliestMessage: false,
        mediaMessageTypes: new Set([]),
        vcardMessageTypes: new Set([]),
        textMessageTypes,
        abortSignal: this.abortController.signal,
        emitAckChanges: false,
      },
      this.logger
    );

    for await (const message of this.scraper) {
      this.addSelectMessageChcekbox(this.getSelectiveSyncStore({ message, allowedMessageTypes: textMessageTypes, ageLimit: Infinity }));
    }
  }

  private emitMessage(
    message: EmittedMessage,
    query: SyncEligibleQuery,
    selectedContact: Pick<SelectedContactType, "phone">,
    syncedMessages: Map<string, number | null>
  ) {
    if (!this.selectors) {
      return;
    }

    const loadMethod = message.loadSignedMedia || message.loadMedia;

    if (!loadMethod) {
      return;
    }

    return loadMethod
      .call(message)
      .then(async (serialized) => {
        const mediaMetadata = serialized.media;
        if (mediaMetadata?.invalid === "size") {
          this.markAsInvalidMessage(serialized);
        }
        if (mediaMetadata?.invalid && query.user?.uploadFile?.ignoreInvalidMediaFiles) {
          return;
        }
        if (!message.text && (!mediaMetadata || mediaMetadata.invalid)) {
          return;
        }

        if (this.isSinglePersonQuery(query)) {
          serialized.templateId = await this.templatesStore.getTemplateId(query.person, message);
          const personId = query.person?.Id ?? "";
          this.trackedMessageIds.get(personId ?? "")?.delete(message.id ?? "");
          this.trackedMessageIds.get(personId ?? "")?.delete(message.shortId);
        }

        this.sendQueue.enqueue(
          toMessageQueueItem(serialized, {
            _id: serialized.serializedMessageId,
            contactPhone: selectedContact?.phone,
            query: getCompactQuery(query),
          })
        );

        query.msgs?.msgIds?.push(message.shortId);
        if (query.msgs?.msgIdLatestUpdate) {
          query.msgs.msgIdLatestUpdate[message.shortId] = message.latestUpdate;
        }
        syncedMessages.set(message.shortId, message.latestUpdate);
        // For now, we disabled this feature. it's commented on purpose. DO-NOT remove it.
        // this.markAsSynced(message.serializedMessageId);
      })
      .catch((error) => {
        this.logger.error({ msgId: message.serializedMessageId, error: error.message }, "emitMessage failed");
      });
  }

  private isSinglePersonQuery(query: QueryResponseType | CreateContactResponseType | CreateLeadResponseType): query is SinglePersonQuery {
    return Boolean(
      query.success &&
        ((query.person && !Array.isArray(query.person)) || (query as SuccessfullQueryResponse).group || (query as SuccessfullQueryResponse).isGroup)
    );
  }

  // #previousState should be used with care, to avoid race conditions
  // it should only be used when it is safe to assume it has not changed
  #previousState: ServiceState | undefined;
  resetState({ query, selectedContact, syncMode }: Partial<ServiceState> = {}) {
    this.abortController?.abort();
    this.abortController = undefined;
    this.scraper = undefined;
    this.selectedMessages.clear();
    this.#previousState = undefined;
    this.cleanupMessagesDOMInstrumentations();

    if (!query || !selectedContact || !this.selectors) {
      return;
    }
    this.MessageEmitter.inject(this.selectors);

    if (this.MessageEmitter.getContactPhone(this.MessageEmitter.getActiveChat()) !== selectedContact.phone) {
      this.logger.warn("selected contact changed during syncronization");
      return;
    }
    if (!isSyncEligibleQuery(query)) {
      return;
    }
    if (!this.selectors || !query.user || !shouldSyncQuery(query, selectedContact?.isGroup)) {
      return;
    }
    if (!syncMode && query.msgs) {
      syncMode = query.msgs.status ? "auto" : "off";
    } else {
      syncMode ??= "off";
    }

    this.#previousState = { query, selectedContact, syncMode };
    this.listenToMessages(query, selectedContact, syncMode);
  }

  private async listenToMessages(query: SyncEligibleQuery, selectedContact: SelectedContactType, syncMode: AutoSyncStatus) {
    const chatModel = this.MessageEmitter.getActiveChat();
    if (!chatModel) {
      return;
    }
    const syncedMessages = new Map(
      query.msgs?.msgIdLatestUpdate ? Object.entries(query.msgs?.msgIdLatestUpdate) : (query.msgs?.msgIds ?? []).map((id) => [id, null])
    );

    const {
      blockedPhones,
      scrollToEarliestMessage,
      mediaMessageMimeTypes,
      mediaMessageTypes,
      vcardMessageTypes,
      eventMessageTypes,
      textMessageTypes,
      maxFileSizeBytes,
      maxMessageAge,
      emitAckChanges,
      collectGroupParticipants,
      syncNewsLetterMessages,
      syncStatusMessages,
    } = MessageSyncService.getListenerSettings(query.user);
    this.abortController = new AbortController();
    this.scraper = new this.MessageEmitter(
      chatModel,
      {
        blockedPhones,
        listenToNewMessages: true,
        mediaMode: "binary",
        maxMessageAge: syncMode === "auto" ? maxMessageAge : Infinity,
        scrollToEarliestMessage: syncMode === "auto" && scrollToEarliestMessage,
        mediaMessageTypes: syncMode === "auto" ? mediaMessageTypes : DEFAULT_SCRAPER_SETTINGS.mediaMessageTypes,
        vcardMessageTypes: syncMode === "auto" ? vcardMessageTypes : DEFAULT_SCRAPER_SETTINGS.vcardMessageTypes,
        eventMessageTypes: DEFAULT_SCRAPER_SETTINGS.eventMessageTypes,
        mediaMessageMimeTypes: syncMode === "auto" ? mediaMessageMimeTypes : undefined,
        textMessageTypes,
        maxFileSizeBytes,
        emitAckChanges,
        abortSignal: this.abortController.signal,
        collectGroupParticipants,
        syncNewsLetterMessages,
        syncStatusMessages,
      },
      this.logger
    );

    const allowedMessageTypes = new Set<ThreadMessageTypeType>([
      ...mediaMessageTypes,
      ...vcardMessageTypes,
      ...textMessageTypes,
      ...DEFAULT_SCRAPER_SETTINGS.pollMessageTypes,
      ...eventMessageTypes,
    ]);
    const trackedMessageIds = this.getTrackedMessageIds(query, query.user);
    let processedCount = 0;
    let syncedCount = 0;
    let channel: string | undefined;

    for await (const message of this.scraper) {
      channel ??= message.channelType;
      processedCount++;
      const shortId = message.shortId;
      const syncedMessageLastUpdate = syncedMessages.get(shortId) || syncedMessages.get(message.serializedMessageId);
      const isMessageSynced = syncedMessages.has(shortId) || syncedMessages.has(message.serializedMessageId);
      const isMessageOutdated = query.user.autoSyncMessageUpdates && syncedMessageLastUpdate && syncedMessageLastUpdate < message.latestUpdate;

      if (isMessageSynced) {
        // For now, we disabled this feature. it's commented on purpose. DO-NOT remove it.
        // this.markAsSynced(message.serializedMessageId);
      }

      if (message.type !== "gp2" && ((syncMode === "auto" && !isMessageSynced) || isMessageOutdated)) {
        syncedCount++;
        this.emitMessage(message, query, selectedContact, syncedMessages);
      }

      if (syncMode === "selective") {
        this.addSelectMessageChcekbox(
          this.getSelectiveSyncStore({ message, allowedMessageTypes, syncedMessages, query, selectedContact, ageLimit: maxMessageAge })
        );
      }

      trackedMessageIds.then(() => this.trackMessage(message, isMessageSynced, query));
    }
    this.logger.info({ processed: processedCount, synced: syncedCount, channel }, "Sync complete");
  }

  private async getTrackedMessageIds(query: SyncEligibleQuery, user: UserType) {
    if (query.user.reportToMultipleDestinations || query.user.auto_sync_to_duplicate_records || !this.isSinglePersonQuery(query)) {
      return;
    }
    const personId = query.person?.Id;
    if (!personId || this.trackedMessageIds.has(personId)) {
      return;
    }
    if (!user?.trackAnalytics) {
      this.trackedMessageIds.set(personId, new Set());
      return;
    }
    try {
      const messages = await backgroundClient.send(BgCommands.GET_KNOWN_MESSAGES, { personId }, window.AbortSignal.timeout(ms("5s")));
      this.trackedMessageIds.set(personId, new Set(messages));
    } catch {
      /* fail silently */
      this.trackedMessageIds.set(personId, new Set());
    }
  }

  private trackMessage(msg: EmittedMessage, isMessageSynced: boolean, rawQuery: SyncEligibleQuery) {
    if (rawQuery.user.reportToMultipleDestinations || rawQuery.user.auto_sync_to_duplicate_records || !this.isSinglePersonQuery(rawQuery)) {
      return;
    }
    const query = getCompactQuery(rawQuery);
    if (!query) {
      return;
    }
    if (
      !msg.id ||
      !query.personId ||
      this.trackedMessageIds.get(query.personId)?.has(msg.id) ||
      this.trackedMessageIds.get(query.personId)?.has(msg.shortId) // Backward compatibility, we used to report short message ids
    ) {
      return;
    }
    this.trackedMessageIds.get(query.personId)?.add(msg.id);

    const loadMethod = msg.loadSignedMedia || msg.loadMedia;

    if (!loadMethod) {
      return;
    }

    return this.templatesStore
      .getTemplateId(rawQuery.person, msg)
      .then((templateId) => {
        return loadMethod.call(msg).then((message) => {
          backgroundClient.sendSync({
            cmd: BgCommands.ANALYTICS_TRACK,
            data: scrapedMessageToAnalyticsMessage({ message, query, isMessageSynced, templateId }),
          });
        });
      })
      .catch((error) => {
        this.logger.error({ messageId: msg.serializedMessageId, error: error.message }, "trackMessage failed");
      });
  }

  async collectAllContactMessages({
    user,
    maxMessageAge,
    query,
  }: {
    user: UserType;
    maxMessageAge?: number;
    query: (contact: { isGroup: boolean; name: string; phone: string }) => Promise<QueryResponseType | undefined>;
  }) {
    const settings = MessageSyncService.getListenerSettings(user);
    const scraper = new this.MessageEmitter(
      AllChats,
      {
        mediaMode: "binary",
        ...settings,
        listenToNewMessages: false,
        scrollToEarliestMessage: true,
        maxMessageAge: maxMessageAge ? Math.min(maxMessageAge, settings.maxMessageAge) : settings.maxMessageAge,
      },
      this.logger
    );
    const queries = new Map<string, Promise<{ query: SyncEligibleQuery; syncedMessages: Map<string, number | null> } | null>>();
    const promises: Promise<void>[] = [];
    for await (const message of scraper) {
      const phone = message.isSentByMe ? message.receiverPhone : message.senderPhone;
      if (!queries.has(phone)) {
        const q = query({ isGroup: message.isGroupMsg, name: (message.isSentByMe ? message.receiverName : message.senderName) ?? "", phone }).then(
          async (query) => {
            if (!query || !isSyncEligibleQuery(query) || !shouldSyncQuery(query, message.isGroupMsg)) {
              return null;
            }
            await this.getTrackedMessageIds(query, query.user);
            const syncedMessages = new Map(
              query.msgs?.msgIdLatestUpdate ? Object.entries(query.msgs?.msgIdLatestUpdate) : (query.msgs?.msgIds ?? []).map((id) => [id, null])
            );
            return { query, syncedMessages };
          }
        );
        queries.set(phone, q);
      }
      promises.push(
        queries.get(phone)!.then((result) => {
          if (!result) return;
          const isMessageSynced = result.syncedMessages.has(message.shortId) || result.syncedMessages.has(message.serializedMessageId);
          this.trackMessage(message, isMessageSynced, result.query);
          if (!isMessageSynced) {
            return this.emitMessage(message, result.query, { phone }, result.syncedMessages);
          }
        })
      );
    }
    await Promise.all(promises);
    this.sendQueue.flush();
    return promises.length;
  }

  init({ i18n, selectors }: { i18n?: Translations; selectors: IMSelectorsUnion }) {
    this.i18n = i18n;
    this.selectors = selectors;
    webScriptBackend
      .on(WebScriptCommands.START_CAPTURE_CHAT_MESSAGES, async () => {
        this.selectAllMessagesWithText();
      })
      .on(WebScriptCommands.STOP_CAPTURE_CHAT_MESSAGES, async () => {
        this.commitSelectiveSyncMessages();
        this.resetState(this.#previousState);
      })
      .on(WebScriptCommands.UPDATE_AUTO_SYNC_STATUS, (data: { newStatus: AutoSyncStatus }) =>
        this.resetState({ ...this.#previousState, syncMode: data.newStatus })
      )
      .on(WebScriptCommands.UPDATE_GROUP_STATE, (group: WhatsllyGroupType) => {
        if (!this.#previousState?.query || !this.#previousState.query.success) {
          throw new Error("Cannot update group state in the current state");
        }
        this.resetState({ selectedContact: this.#previousState?.selectedContact, query: { ...this.#previousState.query, group } });
      })
      .on(WebScriptCommands.PERSON_CREATED, (data) => this.resetState({ query: data.person, selectedContact: data.selectedContact }))
      .on(WebScriptCommands.COMMIT_SELECTED_MESSAGES, () => this.commitSelectiveSyncMessages())
      .on(WebScriptCommands.LOGOUT, () => this.resetState(undefined));
    setInterval(() => this.instrumentDOM(), ms("3s"));
  }
}
